import classNames from "classnames";
import {
	DragOverEventArgs,
	DragOverEventResult,
	DropEventArgs,
	useCallbackWithoutArguments,
	useDragAndDropEvents
} from "client/Hooks";
import {
	EMPTY_ARRAY,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import {
	forwardRef,
	PropsWithChildren,
	ReactElement,
	Ref,
	useMemo
} from "react";
import {
	DimensionRecommendationKind,
	ImageSelectionPlaceholder,
	FormattedText,
	Typography
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { BlobIdImageHubMediaComposerViewModel } from "client/Api/Hub/common.composer";
import { getHubImageUrl } from "client/Api/Hub/common";
import { UserHtml } from "client/Features/UserHtml/UserHtml";
import styles from "./HubMediaSelectLayout.module.less";
import { VideoViewer } from "../Builder/Viewer/Elements/Video/Video";
import { hubItemKindToHubDataSourceMap } from "../Sidebar/common";

export type HubMediaSelectLayoutProps = {
	trackingId: string;
} & ({
	isReadonly: true;
	onClick?: undefined;
	onFocus?: undefined;
	onBlur?: undefined;
} | {
	isReadonly?: undefined;
	onClick(): void;
	onFocus(): void;
	onBlur(): void;
	onDragOver?(args: DragOverEventArgs): DragOverEventResult;
	onDrop?(args: DropEventArgs): void | Promise<void>;
});

const HubMediaSelectLayoutInternal = forwardRef(
	(props: PropsWithChildren<HubMediaSelectLayoutProps>, ref: Ref<HTMLDivElement>): ReactElement => {
		const {
			isReadonly = undefined,
			onBlur: onBlurFromProps,
			onClick: onClickFromProps,
			onFocus: onFocusFromProps,
			trackingId,
			children
		} = props;

		const onBlur: (() => void) | undefined = useCallbackWithoutArguments(onBlurFromProps);
		const onClick: (() => void) | undefined = useCallbackWithoutArguments(onClickFromProps);
		const onFocus: (() => void) | undefined = useCallbackWithoutArguments(onFocusFromProps);

		const {
			isDropTarget,
			...dragAndDropEventHandlers
		} = useDragAndDropEvents(!props.isReadonly
			? {
				onDragOver: props.onDragOver,
				onDrop: props.onDrop
			} :
			{}
		);

		return (
			<div
				{...setTrackingId(trackingId)}
				{...dragAndDropEventHandlers}
				ref={ref}
				className={classNames(
					styles.root,
					{
						[styles.root_readonly]: isReadonly,
						[styles.root_isDragging]: isDropTarget
					}
				)}
				tabIndex={1}
				onClick={onClick}
				onFocus={onFocus}
				onBlur={onBlur}
			>
				{children}
			</div>
		);
	}
);
HubMediaSelectLayoutInternal.displayName = "HubMediaSelectLayout";

type NoneMediaContentProps ={
	maxImageSize?: number | null;
};

const NoneMediaContent = (props: NoneMediaContentProps): ReactElement => {
	const { maxImageSize } = props;

	return (
		<div
			{...setTrackingId("no-media")}
			className={styles.noMediaContent}
		>
			<ImageSelectionPlaceholder
				trackingId="none-media-placeholder"
				kind="default"
				title={(
					<FormattedText
						text={localize("_AddMediaInfo")}
						thingsToReplace={EMPTY_ARRAY}
						thingsToWrap={
							[
								{
									blockId: "DragAndDrop",
									wrap: (children) => (
										<Typography weight="bold">
											{children}
										</Typography>
									)
								},
								{
									blockId: "Click",
									wrap: (children) => (
										<Typography weight="bold">
											{children}
										</Typography>
									)
								},
							]
						}
					/>
				)}
				maxFileSize={maxImageSize}
				dimensionRecommendation={{
					kind: DimensionRecommendationKind.DimensionWithAspectRatio,
					height: 576,
					width: 1024,
					aspectRatio: "16:9"
				}}
				supportedFormats={[
					localize("_FileExtensionJpg"),
					localize("_FileExtensionPng"),
					localize("_FileExtensionGif")
				]}
			/>
		</div>
	);
};
NoneMediaContent.displayName = "HubMediaSelectLayout.NoneMediaContent";

export type ImageMediaContentOriginalImage = Omit<BlobIdImageHubMediaComposerViewModel, "focalPoint">;

type ImageMediaContentProps = {
	hubItemKind: HubItemKind;
	image: OptionalModel<Base64FileUriUploadModel>;
	originalImageMedia: ImageMediaContentOriginalImage | null;
	hasError?: boolean;
};

const ImageMediaContent = (props: ImageMediaContentProps): ReactElement | null => {
	const {
		hubItemKind,
		image,
		originalImageMedia,
		hasError
	} = props;

	const imageInfo = useMemo(
		(): [
			src: string,
			fileName: string
		] | null => {
			if (image.isSet) {
				return [
					image.value
						.uri,
					image.value
						.filename
				];
			}

			if (isNotNil(originalImageMedia)) {
				return [
					getHubImageUrl(
						hubItemKindToHubDataSourceMap[hubItemKind],
						originalImageMedia.imageId
					),
					originalImageMedia.imageName
				];
			}

			return null;
		},
		[
			image,
			hubItemKind,
			originalImageMedia
		]
	);

	if (!imageInfo) {
		return null;
	}

	const [src, fileName] = imageInfo;
	const trackingId: string = "image-media";

	if (hasError) {
		return (
			<ImageSelectionPlaceholder
				kind="error"
				trackingId={trackingId}
			/>
		);
	}

	return (
		<div
			{...setTrackingId(trackingId)}
			className={styles.imageContent}
		>
			<img
				src={src}
				alt={fileName}
			/>
		</div>
	);
};
ImageMediaContent.displayName = "HubMediaSelectLayout.ImageMediaContent";

type VideoMediaContentProps = {
	videoUrl: string;
};

const VideoMediaContent = (props: VideoMediaContentProps): ReactElement => {
	const {
		videoUrl
	} = props;

	return (
		<div
			{...setTrackingId("video-content-media")}
			className={styles.videoContent}
		>
			<VideoViewer url={videoUrl} />
		</div>
	);
};
VideoMediaContent.displayName = "HubMediaSelectLayout.VideoMediaContent";

type EmbedMediaContentProps = {
	embedCode: string;
};

const EmbedMediaContent = (props: EmbedMediaContentProps): ReactElement => {
	const {
		embedCode
	} = props;

	const trackingId: string = "embed-content-media";

	return (
		<div
			{...setTrackingId(trackingId)}
			className={styles.embedContent}
		>
			<UserHtml
				html={embedCode}
				trackingId="user-html"
			/>
		</div>
	);
};
EmbedMediaContent.displayName = "HubMediaSelectLayout.EmbedMediaContent";

type HubMediaSelectLayoutWithSubComponents = {
	(props: PropsWithChildren<HubMediaSelectLayoutProps> & { ref?: Ref<HTMLDivElement>; }): ReactElement;
	displayName: string;
	NoneMediaContent: typeof NoneMediaContent;
	ImageMediaContent: typeof ImageMediaContent;
	VideoMediaContent: typeof VideoMediaContent;
	EmbedMediaContent: typeof EmbedMediaContent;
};

export const HubMediaSelectLayout = HubMediaSelectLayoutInternal as unknown as HubMediaSelectLayoutWithSubComponents;
HubMediaSelectLayout.NoneMediaContent = NoneMediaContent;
HubMediaSelectLayout.ImageMediaContent = ImageMediaContent;
HubMediaSelectLayout.VideoMediaContent = VideoMediaContent;
HubMediaSelectLayout.EmbedMediaContent = EmbedMediaContent;